import React from "react";
import { useNavigate } from "react-router-dom";
import { FileText, FileType2, Clock } from "lucide-react";
import { PolicyStatusBadge, fmtDate } from "./PolicyBadges";

export const PolicyTable = ({ policies, loading }) => {
  const navigate = useNavigate();
  if (loading) return <div className="nv-card p-10 text-center text-sm text-slate-500 animate-pulse">Loading policies…</div>;
  if (!policies.length) {
    return (
      <div className="nv-card p-12 text-center" data-testid="policy-empty-state">
        <FileText className="mx-auto text-slate-300" size={36} />
        <p className="mt-3 text-sm font-medium text-slate-700">No policies match these filters</p>
        <p className="text-xs text-slate-500 mt-1">Try a different search, or clear the category / home / status filters.</p>
      </div>
    );
  }
  return (
    <div className="nv-card overflow-x-auto" data-testid="policy-table">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
          <tr>
            <th className="px-4 py-3">Policy</th>
            <th className="px-4 py-3">Category</th>
            <th className="px-4 py-3">Owner</th>
            <th className="px-4 py-3">Version</th>
            <th className="px-4 py-3">Status</th>
            <th className="px-4 py-3">Effective</th>
            <th className="px-4 py-3">Review due</th>
            <th className="px-4 py-3">Scope</th>
          </tr>
        </thead>
        <tbody>
          {policies.map((p) => {
            const overdue = p.review_date && new Date(p.review_date) < new Date() && p.status === "Active";
            return (
              <tr key={p.id} onClick={() => navigate(`/policies/${p.id}`)} data-testid={`policy-row-${p.id}`}
                className="border-t border-slate-100 hover:bg-sky-50/60 cursor-pointer transition-colors">
                <td className="px-4 py-3">
                  <div className="flex items-start gap-2">
                    <FileType2 size={16} className="mt-0.5 text-slate-400 shrink-0" />
                    <div>
                      <p className="font-medium text-slate-900">{p.title}</p>
                      {p.description && <p className="text-xs text-slate-500 line-clamp-1">{p.description}</p>}
                      {p.match === "content" && <span className="text-[11.5px] text-sky-700 font-medium">matched in document text</span>}
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3 text-slate-700">{p.category}</td>
                <td className="px-4 py-3 text-slate-700">{p.owner || "—"}</td>
                <td className="px-4 py-3">
                  <span className="font-mono text-xs">{p.version_label}</span>
                  {p.has_pending_version && <span className="ml-1.5 text-[11.5px] text-amber-700" data-testid={`pending-version-${p.id}`}>new version pending</span>}
                </td>
                <td className="px-4 py-3"><PolicyStatusBadge status={p.status} /></td>
                <td className="px-4 py-3 text-slate-600">{fmtDate(p.effective_date)}</td>
                <td className={`px-4 py-3 ${overdue ? "text-rose-700 font-medium" : "text-slate-600"}`}>
                  <span className="inline-flex items-center gap-1">{overdue && <Clock size={13} />}{fmtDate(p.review_date)}</span>
                </td>
                <td className="px-4 py-3 text-xs text-slate-600">
                  {(p.home_scope?.length ? p.home_scope.join(", ") : "All homes")}
                  <span className="block text-[11.5px] text-slate-400">{p.all_roles ? "All roles" : (p.permitted_roles || []).join(", ")}</span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};
